import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { StreamingSource } from 'fym-shared/src/music24/streaming';
import { TypedMemo } from '../../../common/types';
import { sourceLook } from '../streamingSources';
import { streamingTotalLabels } from '../streamingTotalsMessages';
import { streamingStateMessages as messages } from '../streamingStateMessages';
import { sourceStateText } from './sourceStateText';
import { SourceMark } from './SourceMark';
import { Panel } from '../../components/primitives/Panel';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The Catalog link of a state that needs an action. */
const CATALOG_LINK = {
  reconnect: messages.reconnectLink,
  noArtist: messages.chooseArtistLink,
  disconnected: messages.connectAgainLink,
} as const;

interface SourceCardProps {
  source: StreamingSource;
  onOpenCatalog: () => void;
}

/**
 * One connected source (design f1): the account, what it gives, the last read and its state.
 * It has no setup control: a state that needs an action links to Catalog.
 */
const SourceCardComponent: React.FC<SourceCardProps> = ({ source, onOpenCatalog }) => {
  const intl = useIntl();
  const look = sourceLook(source.integrationType);
  const isProblem = source.state === 'reconnect' || source.state === 'failed';

  const gives = useMemo(() => {
    const labels = source.gives.map((key) => intl.formatMessage(streamingTotalLabels[key]));
    if (source.kind === 'distributor') {
      labels.push(
        intl.formatMessage(messages.givesStores),
        intl.formatMessage(messages.givesCountries),
        intl.formatMessage(messages.givesPlaylists),
        intl.formatMessage(messages.givesEarnings)
      );
    }
    return labels;
  }, [intl, source.gives, source.kind]);

  return (
    <Panel nested className={styles.sourceCard} data-testid={`streaming-source-${source.integrationType}`}>
      <div className={styles.sourceCardHead}>
        <SourceMark integrationType={source.integrationType} />
        <span>{look.name}</span>
        <span className={styles.faint}>
          {intl.formatMessage(source.kind === 'distributor' ? messages.kindDistributor : messages.kindPortal)}
        </span>
      </div>
      {source.artistName !== null ? <span className={styles.faint}>{source.artistName}</span> : null}
      <div className={styles.gives}>
        {gives.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
      <span className={clsx(isProblem ? styles.stateWarn : styles.faint)}>
        {withBrandWord(sourceStateText(intl, source))}
      </span>
      {source.state === 'reconnect' || source.state === 'noArtist' || source.state === 'disconnected' ? (
        <button type="button" className={styles.linkButton} onClick={onOpenCatalog}>
          {intl.formatMessage(CATALOG_LINK[source.state])}
        </button>
      ) : null}
    </Panel>
  );
};

export const SourceCard = TypedMemo(SourceCardComponent);
